
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { NodeType } from '@amplify/amp-module';
import { isEmpty } from 'lodash';
import { useNodeUtils } from '@/composables/useNodeUtils';
import { GetAmpById } from '@/constants/actions';
import NodeWrapper from '@/components/graph/common/NodeWrapper.vue';
import CardLabel from '@/components/common/CardLabel.vue';
import ConversationThumbnail from '@/components/common/ConversationThumbnail';
import SelectConversationPopup from '@/components/graph/popups/SelectConversationPopup.vue';
import { GetConversationsList } from '@/constants/getters.js';
import { useStoreInNodes } from '@/composables/useStoreInNodes';

export default {
  components: {
    CardLabel,
    ConversationThumbnail,
    NodeWrapper,
    SelectConversationPopup,
  },

  setup() {
    const nodeType = NodeType.open;
    const nodeActions = [{ click: true, key: 'bottom' }];

    const { store } = useStoreInNodes();
    const {
      ampData,
      uiData,
      nodeId,
      nodeRef,
      nodeSelf,
      canEditAmp,
      bringNodeToFront,
      emitNodeRenderDoneEvent,
      updateDelay,
    } = useNodeUtils({ nodeType });

    const showConversationsPopup = ref(false);
    const selectedAmp = ref({});
    const loading = ref(false);
    const readyElements = ref(0);

    const isDone = computed(() => !!ampData.value.ampId && !!ampData.value.conversationId);
    const amps = computed(() => store.value.state.ampStore.amps);
    const selectedConversation = computed(() => {
      const { conversationId } = ampData.value;

      return (
        (conversationId &&
          selectedAmp.value?.data?.conversations &&
          getConversationsList(selectedAmp.value.data.conversations)?.find((conv) => conv.id === conversationId)) ||
        {}
      );
    });
    const isAmpLive = computed(() => isLive(selectedAmp.value));

    const getConversationsList = (conversations) =>
      store.value.getters[`builderStore/${GetConversationsList}`](conversations);

    onMounted(async () => {
      const { ampId, conversationId } = ampData.value;

      if (ampId) {
        if (!conversationId) {
          ampData.value.ampId = '';
          return;
        }

        selectedAmp.value = amps.value.find((amp) => amp.id === ampId) || null;

        if (!selectedAmp.value) {
          loading.value = true;
          selectedAmp.value = await store.value.dispatch(`ampStore/${GetAmpById}`, { id: ampId });
          loading.value = false;
        }

        const conversation = Object.values(selectedAmp.value?.data?.conversations || {})?.find(
          (conv) => conv.id === conversationId
        );

        if (!selectedAmp.value || !conversation) {
          ampData.value.ampId = '';
          ampData.value.conversationId = '';
        }
      }

      if (!ampId) {
        await nextTick();
        emitNodeRenderDoneEvent();
      }
    });

    const selectConversation = (data) => {
      const { conversation, amp } = data;
      ampData.value.conversationId = conversation.id;
      ampData.value.ampId = amp.id;
      selectedAmp.value = amp;
      toggleAmpsPopup();
    };

    const toggleAmpsPopup = () => {
      if (!canEditAmp.value) {
        return;
      }

      showConversationsPopup.value = !showConversationsPopup.value;

      if (showConversationsPopup.value) {
        bringNodeToFront();
      }
    };

    const isLive = (amp) => amp?.metadata?.isLive;

    const resizeNode = (value) => {
      const { width, height } = nodeSelf.value.size();

      nodeSelf.value.resize(width, height + value);
    };

    const onElementReady = async () => {
      readyElements.value += 1;

      // 1 image
      if (readyElements.value === 1) {
        await nextTick();
        emitNodeRenderDoneEvent();
      }
    };

    watch(selectedAmp, (amp, oldAmp) => {
      if ((isEmpty(oldAmp) && amp && !isLive(amp)) || (isLive(oldAmp) && !isLive(amp))) {
        resizeNode(28);
      } else if (!isLive(oldAmp) && isLive(amp)) {
        resizeNode(-28);
      }
    });

    return {
      ampData,
      canEditAmp,
      isAmpLive,
      isDone,
      loading,
      nodeActions,
      nodeId,
      nodeRef,
      nodeType,
      selectedAmp,
      selectedConversation,
      showConversationsPopup,
      uiData,
      onElementReady,
      selectConversation,
      toggleAmpsPopup,
      updateDelay,
    };
  },
};
